import { Label } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { WhereaboutsModal } from "shared/components/root";
import { Flex } from "styled-system/jsx";
import { Input, Tag, Text, useToastContext } from "@ttc3k/trekker";
import { Collapsible } from "shared/components/molecules";
import { Variants } from "framer-motion";
import { usePeopleTagsManageModal } from "./usePeopleTagsManageModal";
import { usePeopleTagsContext } from "../PeopleTagsContext";
import { TagDetailsFlyout } from "apps/adminland/directories/tagManager/flyouts";
import { useEffect, useState } from "react";

type PeopleTagsManageModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const PeopleTagsManageModal = ({
  isOpen,
  onClose,
}: PeopleTagsManageModalProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "HEADER.TAGS.MODAL",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();

  const {
    isSaving,
    isCreatingTag,
    setIsCreatingTag,
    newTagValue,
    setNewTagValue,
    handleSave,
    handleClose,
    refreshQueries,
  } = usePeopleTagsManageModal(isOpen, onClose);

  const {
    editingTag,
    setEditingTag,
    allTagGroupsWithTags,
    flyoutCategoryOptions,
    onTagUpdated,
    onTagDeleted,
    onTagSplitCompleted,
    onTagMergeCompleted,
  } = usePeopleTagsContext();

  // This is a crazy workaround hack. Previously, I was just setting the TagDetailsFlyout
  // isOpen to !!editingTag, but when setting editingTag to null, it ALSO closed the WhereaboutsModal.
  const [isTagDetailsFlyoutOpen, setIsTagDetailsFlyoutOpen] = useState(false);
  useEffect(() => {
    setIsTagDetailsFlyoutOpen(!!editingTag);
  }, [editingTag]);

  return (
    <>
      <WhereaboutsModal
        isOpen={isOpen}
        onClose={() => {
          handleClose();
        }}
        title={t("TITLE")}
        description={t("DESCRIPTION")}
        HeaderIcon={Label}
        cancelButtonCopy={t("CLOSE")}
        cancelButtonProps={{
          onClick: handleClose,
          disabled: isSaving,
          type: "button",
        }}
        saveButtonCopy={t("SAVE")}
        saveButtonProps={{
          visual: "accent.blue",
          isIconRightAligned: true,
          disabled: !newTagValue,
          isLoading: isSaving,
          onClick: handleSave,
          type: "button",
        }}
        size={"md"}
      >
        <Flex flexDir="column" gap="200">
          {/* Existing tags + add tag button */}
          {allTagGroupsWithTags[0]?.tags.length > 0 && (
            <>
              <Flex alignItems="center" gap="100" flexWrap="wrap">
                {allTagGroupsWithTags[0]?.tags.map(tag => (
                  <Tag
                    key={tag.id}
                    visual="accent.purple"
                    mode="light"
                    tabIndex={0}
                    disabled={isSaving}
                    onClick={() => setEditingTag(tag)}
                    onContextMenuCapture={(
                      e: React.MouseEvent<HTMLDivElement>,
                    ) => {
                      e.preventDefault();
                      navigator.clipboard.writeText(tag.id);
                      toastFactory.create({
                        title: tCore("SUCCESS"),
                        description: t("TAG_ID_COPIED", { tagId: tag.id }),
                      });
                    }}
                    flexShrink={0}
                  >
                    {`${tag.label} (${tag.usage?.total ?? 0})`}
                  </Tag>
                ))}
                <Tag
                  visual="primary"
                  mode="light"
                  tabIndex={0}
                  disabled={isSaving}
                  onClick={() => setIsCreatingTag(true)}
                  flexShrink={0}
                >
                  {t("ADD_TAG")}
                </Tag>
              </Flex>
              <Text visual="smallRegular" color="text.light">
                {t("COUNTS_REFRESHED")}
              </Text>
            </>
          )}

          {/* Add tag form */}
          <Collapsible
            open={isCreatingTag}
            additionalVariantProps={
              {
                open: {
                  display: "block",
                },
                closed: {
                  display: "none",
                },
              } as Variants
            }
          >
            <Input
              label={t("ADD_TAG_LABEL")}
              value={newTagValue}
              disabled={isSaving}
              onChange={e => setNewTagValue(e.target.value)}
            />
          </Collapsible>
        </Flex>
      </WhereaboutsModal>

      <TagDetailsFlyout
        isOpen={isTagDetailsFlyoutOpen}
        onClose={() => {
          setEditingTag(null);
          setIsTagDetailsFlyoutOpen(false);
        }}
        selectedTagId={editingTag?.id ?? ""}
        allTagGroupsWithTags={allTagGroupsWithTags}
        flyoutCategoryOptions={flyoutCategoryOptions}
        onTagUpdated={onTagUpdated}
        onTagDeleted={onTagDeleted}
        onTagSplitCompleted={onTagSplitCompleted}
        onTagMergeCompleted={onTagMergeCompleted}
        tagMutationCompleteRefreshesQueries={refreshQueries}
      />
    </>
  );
};
